import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import {
  MGapCol,
  SGapCol,
  XxsGapCol,
} from '@xing-com/crate-jobs-components-columns';
import {
  JobTeaserFacts,
  useAriaLabel,
} from '@xing-com/crate-jobs-components-job-teaser-options';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import {
  useBreakpoints,
  useJobTeaserClickExternalTracking,
} from '@xing-com/crate-jobs-hooks';
import type { JobTeaserListItemProps } from '@xing-com/crate-jobs-xinglets';

import { Highlight } from './highlight';
import * as S from './job-teaser-list-item.styles';

export const JobTeaserListItem: React.FC<JobTeaserListItemProps> = ({
  job,
  isNew,
  url,
  matchingHighlights,
  className,
  onClick: clickCallback,
  onContextMenu,
  onAuxClick,
  actions,
  'data-testid': dataTestId,
  headingLevel = 'h3',
  showEmploymentType,
  showRatingMarker,
  showRefreshedDate,
  'aria-label': ariaLabel,
  highlight,
}) => {
  const {
    id,
    activatedAt,
    refreshedAt,
    title,
    location,
    companyName,
    companyLogo,
    redirectsToThirdPartyUrl,
    applyUrl,
  } = selectJobData(job);

  const ariaLabelText = useAriaLabel({
    ariaLabel: ariaLabel ?? title,
    redirectsToThirdPartyUrl,
    target: '_blank',
  });
  const onClick = useJobTeaserClickExternalTracking({
    isExternalJob: redirectsToThirdPartyUrl,
    jobId: id,
    url,
    clickCallback,
  });

  const { isMediaSmallOrTiny } = useBreakpoints();

  const HeaderColComponent = isMediaSmallOrTiny ? SGapCol : MGapCol;
  const date = showRefreshedDate ? refreshedAt : activatedAt;

  return (
    <S.Card
      forwardedAs="article"
      href={redirectsToThirdPartyUrl && applyUrl ? applyUrl : url}
      linkProps={{
        'aria-label': ariaLabel,
        target: '_blank',
        onClick,
        onContextMenu,
        onAuxClick,
        rel: redirectsToThirdPartyUrl
          ? 'noopener nofollow sponsored'
          : undefined,
      }}
      data-testid={dataTestId}
      className={className}
      aria-label={ariaLabelText}
    >
      <S.Image
        type="company"
        profileName={companyName}
        size="small"
        sizeConfined="large"
        src={companyLogo ?? ''}
        // @ts-expect-error FIXME: SC6
        title={null}
        alt=""
        aria-hidden
      />
      <S.Content>
        <S.Header>
          <HeaderColComponent>
            {redirectsToThirdPartyUrl ? (
              <S.Marker variant="highlight">
                <AriaExtendedText
                  item={{ id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT' }}
                  a11yItem={{
                    id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT_A11Y',
                  }}
                />
              </S.Marker>
            ) : matchingHighlights?.highlight ? (
              <S.Marker variant="highlight">
                {matchingHighlights.highlight.localization.localizationValue}
              </S.Marker>
            ) : null}
            <XxsGapCol>
              <S.Title
                size="medium"
                sizeWide="large"
                forwardedAs={headingLevel}
                data-testid="job-teaser-list-title"
                noMargin
              >
                {title}
              </S.Title>
              {location?.city ? (
                <S.City size="medium" lineHeight="dense" noMargin>
                  {location.city}
                </S.City>
              ) : null}
              <S.Company size="medium" lineHeight="dense" noMargin>
                {companyName}
              </S.Company>
            </XxsGapCol>
          </HeaderColComponent>
        </S.Header>

        <S.BottomCol>
          <JobTeaserFacts
            job={job}
            matchingHighlights={matchingHighlights}
            showEmploymentType={showEmploymentType}
            showRatingMarker={showRatingMarker}
          />
          <Highlight highlight={highlight} />
          {date ? <S.PublicationDate date={date} isNew={isNew} /> : null}
        </S.BottomCol>
      </S.Content>
      {actions}
    </S.Card>
  );
};
